import React, { FC } from 'react';
import { Box, CircularProgress, Link, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { CloseIcon, InfoFilledIcon } from 'src/shared/components';
import { LocalStorageEnums } from 'src/shared/enums';
import { useLocalStorage, useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';
import { updatePreferences } from 'src/store/user/user.actions';

export const EssBanner: FC = () => {
  const theme = useTheme();
  const { t } = useTranslate();
  const dispatch = useThunkDispatch();
  const { preferences, submittingPreferences } = useSelector((state: RootState) => state.user);
  const { value: isBannerHidden, setValue } = useLocalStorage<boolean>(LocalStorageEnums.ESS_BANNER_HIDDEN);

  if (isBannerHidden || preferences?.essAutoRedirect) {
    return null;
  }

  const handleSetToDefault = async () => {
    try {
      if (submittingPreferences) {
        return;
      }

      await dispatch(updatePreferences({ essAutoRedirect: true }));
      toast.info(`${t('settings.congratulations')} ${t('settings.newAppDefaultIsSet')}`, { type: toast.TYPE.SUCCESS });
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), { type: toast.TYPE.ERROR });
    }
  };

  return (
    <Box
      className="p-3 rounded d-flex align-items-center justify-content-between"
      sx={{
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main,
      }}
    >
      <div className="d-flex align-items-center">
        <InfoFilledIcon size={20} color={theme.palette.primary.main} />
        <p className="mb-0 mx-2 fw-500">{t('settings.likingTheNewExperience')}</p>
        <Link
          id="set-to-default-btn"
          className="fw-500"
          component="button"
          underline="none"
          onClick={handleSetToDefault}
        >
          {t('settings.setThisAsYourNewDefault')}
        </Link>
      </div>
      <div>
        {submittingPreferences ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          <Link
            component="button"
            underline="none"
            color={theme.palette.mode === 'light' ? theme.palette.grey['700'] : theme.palette.text.primary}
            onClick={() => setValue(true)}
          >
            <CloseIcon size={20} />
          </Link>
        )}
      </div>
    </Box>
  );
};
